import { FileInfo, FileStatus, FileTableItem } from "@dashboard/files/types";
import semverService from "@services/semverService/semverService";

export const hasApkMetadata = (file: FileInfo) => {
    const packageExists = file.metadata?.file?.apk?.manifest?.package;
    const versionCodeExists = file.metadata?.file?.apk?.manifest?.versioncode;
    return Boolean(packageExists && versionCodeExists);
};

export type FileInfoWithVersions = FileInfo & {
    olderVersions: FileInfo[];
};

export const normalizeFiles = (files: FileInfo[]): FileInfoWithVersions[] => {
    const apkGroups: Record<string, FileInfo[]> = {};
    const nonApk: Record<string, FileInfo> = {};

    for (const file of files) {
        if (!hasApkMetadata(file)) {
            nonApk[`${file.filename}-${file.createdDate}`] = file;
            continue;
        }
        const pkg = file.metadata?.file?.apk?.manifest?.package || "";
        (apkGroups[pkg] ??= []).push(file);
    }

    const apkResults: FileInfoWithVersions[] = Object.values(apkGroups).map((group) => {
        const sorted = [...group].sort((a, b) => {
            const va = String(a.metadata?.file?.apk?.manifest?.versioncode ?? "0");
            const vb = String(b.metadata?.file?.apk?.manifest?.versioncode ?? "0");
            return semverService.compare(vb, va);
        });
        const [main, ...rest] = sorted;
        const seen = new Set<string>([String(main.metadata?.file?.apk?.manifest?.versioncode ?? "0")]);
        const olderVersions = rest.filter((file) => {
            const versioncode = String(file.metadata?.file?.apk?.manifest?.versioncode ?? "0");
            if (seen.has(versioncode)) {
                return false;
            }
            seen.add(versioncode);
            return true;
        });
        return { ...main, olderVersions };
    });

    const nonApkResults: FileInfoWithVersions[] = Object.values(nonApk).map((file) => ({
        ...file,
        olderVersions: [],
    }));

    return [...apkResults, ...nonApkResults];
};

export const isApk = (filename: string) => filename.toLowerCase().endsWith(".apk");

export const createVersionCodeTag = (file: FileTableItem | FileInfo | null) => {
    const { versioncode } = file?.metadata?.file?.apk?.manifest ?? {};
    if (versioncode) {
        return {
            type: "scoped",
            key: "version",
            value: versioncode,
        } as API.Tag;
    }

    return null;
};

const isTerminalAnalyzerStatus = (status: API.AnalyzerStatus) => status === "successful" || status === "ignored";

export const hasAnalyzerInProgress = (data: FileInfo | FileInfo[]): boolean => {
    if (Array.isArray(data)) {
        return data.some((item) => hasAnalyzerInProgress(item));
    }
    if (data.status === FileStatus.Pending) {
        return true;
    }
    if (!data.analyzers) {
        return false;
    }
    return data.analyzers.some((analyzer) => !isTerminalAnalyzerStatus(analyzer.status));
};

export const hasFailedAnalyzer = (data: FileInfo | FileInfo[]): boolean => {
    if (Array.isArray(data)) {
        return data.some((item) => hasFailedAnalyzer(item));
    }
    if (!data.analyzers) {
        return false;
    }
    return data.analyzers.some((analyzer) => analyzer.status === "failed");
};

export const hasUnsuccessfulAnalyzer = (data: FileInfo | FileInfo[]): boolean => {
    return hasAnalyzerInProgress(data) || hasFailedAnalyzer(data);
};
